import React, { useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { useParams } from 'react-router-dom';
import * as mutations from '../graphql/mutations';
import { nowSeconds } from '../platform/events/operationEvents';
import useShopSettings from '../platform/settings/useShopSettings';
import './EstimateApprovePage.css';

const client = generateClient();

const listTrucksWithEstimates = /* GraphQL */ `
  query ListTrucksWithEstimates($limit: Int, $nextToken: String) {
    listTrucks(limit: $limit, nextToken: $nextToken) {
      items {
        id
        repairOrder
        companyName
        truckNumber
        contactPerson
        phone
        email
        year
        make
        model
        vin
        mileage
        status
        parts {
          id
          description
          partNumber
          quantity
          price
        }
        activities {
          id
          type
          text
          author
          timestamp
          photos
          customerFacing
          category
          stage
        }
        estimates {
          id
          truckId
          roNumber
          status
          lines {
            title
            complaint
            cause
            correction
            taxExempt
            laborLines {
              id
              description
              catalogItemId
              hours
              rate
              flatPrice
              amount
              techName
              notes
            }
            partLineIds
          }
          shopSupplies
          shopSuppliesPct
          taxRate
          subtotal
          tax
          total
          notes
          customerNotes
          token
          sentAt
          approvedAt
          approvalMethod
          approvedBy
          approvalNotes
          declinedAt
          declineReason
          invoicedAt
          lockedAt
          paymentTermsDays
          payments {
            id
            amount
            method
            reference
            note
            receivedAt
            recordedBy
          }
          createdBy
          createdAt
          updatedAt
          version
        }
      }
      nextToken
    }
  }
`;

function clean(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(clean);
  const { __typename, ...rest } = obj;
  return Object.fromEntries(Object.entries(rest).map(([key, value]) => [key, clean(value)]));
}

function money(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '$0.00';
}

function nowId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function normalizeToken(token) {
  return decodeURIComponent(token || '').trim();
}

function unitLabel(truck = {}) {
  return [
    truck.truckNumber ? `Unit ${truck.truckNumber}` : '',
    [truck.year, truck.make, truck.model].filter(Boolean).join(' '),
    truck.vin ? `VIN ${truck.vin}` : '',
  ].filter(Boolean).join(' | ');
}

async function findEstimateByToken(token) {
  let nextToken = null;
  do {
    const result = await client.graphql({
      query: listTrucksWithEstimates,
      variables: { limit: 200, nextToken },
    });
    const page = result.data?.listTrucks;
    const found = (page?.items || []).find(truck =>
      (truck?.estimates || []).some(estimate => estimate?.token === token)
    );
    if (found) {
      return {
        truck: found,
        estimate: (found.estimates || []).find(estimate => estimate?.token === token),
      };
    }
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return { truck: null, estimate: null };
}

function EstimateDocument({ truck, estimate, settings }) {
  const ops = estimate?.lines || [];
  const multiOp = ops.length > 1;
  const logo = settings?.branding?.logoUrl || settings?.branding?.logoBase64;
  // Customer paper carries the shop's name, never the software's
  const shopName = settings?.business?.shopName || settings?.business?.companyName || '';

  return (
    <article className="eap-doc">
      <header className="eap-doc-header">
        <div>
          {logo && <img className="eap-logo" src={logo} alt="" />}
          <h1>{shopName}</h1>
          <p>Estimate for RO {truck.repairOrder}</p>
        </div>
        <strong>{money(estimate.total)}</strong>
      </header>

      <section className="eap-grid">
        <div>
          <span>Customer</span>
          <strong>{truck.companyName}</strong>
          {truck.contactPerson && <p>{truck.contactPerson}</p>}
          {truck.phone && <p>{truck.phone}</p>}
          {truck.email && <p>{truck.email}</p>}
        </div>
        <div>
          <span>Unit</span>
          <strong>{unitLabel(truck) || 'Unit information pending'}</strong>
        </div>
      </section>

      {ops.map((line, i) => {
        const labor = line.laborLines || [];
        const parts = (truck?.parts || []).filter(part => (line.partLineIds || []).includes(part.id));
        if (!line.title && !line.complaint && labor.length === 0 && parts.length === 0) return null;
        return (
          <div key={i}>
            {(multiOp || line.title) && (
              <h2 className="eap-op-title">
                {multiOp ? `Operation ${i + 1}` : ''}{line.title ? `${multiOp ? ' — ' : ''}${line.title}` : ''}
              </h2>
            )}

            {(line.complaint || line.cause || line.correction) && (
              <section className="eap-section">
                {line.complaint && <div><span>Complaint</span><p>{line.complaint}</p></div>}
                {line.cause && <div><span>Cause</span><p>{line.cause}</p></div>}
                {line.correction && <div><span>Correction</span><p>{line.correction}</p></div>}
              </section>
            )}

            {labor.length > 0 && (
              <section className="eap-section">
                <h2>Labor</h2>
                <table>
                  <tbody>
                    {labor.map(lineItem => (
                      <tr key={lineItem.id}>
                        <td>{lineItem.description}</td>
                        <td>{lineItem.hours ? `${lineItem.hours} hr` : 'Flat'}</td>
                        <td>{money(lineItem.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {parts.length > 0 && (
              <section className="eap-section">
                <h2>Parts</h2>
                <table>
                  <tbody>
                    {parts.map(part => (
                      <tr key={part.id}>
                        <td>{part.description}{part.partNumber ? ` #${part.partNumber}` : ''}</td>
                        <td>Qty {part.quantity || 1}</td>
                        <td>{money((parseFloat(part.price) || 0) * (parseFloat(part.quantity) || 1))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </div>
        );
      })}

      <section className="eap-totals">
        <div><span>Subtotal</span><strong>{money(estimate.subtotal)}</strong></div>
        {estimate.tax > 0 && <div><span>Tax</span><strong>{money(estimate.tax)}</strong></div>}
        <div className="eap-total"><span>Total</span><strong>{money(estimate.total)}</strong></div>
      </section>

      {estimate.customerNotes && <p className="eap-notes">{estimate.customerNotes}</p>}
    </article>
  );
}

export default function EstimateApprovePage() {
  const { token } = useParams();
  const cleanToken = useMemo(() => normalizeToken(token), [token]);
  const settings = useShopSettings(state => state.settings);
  const loadSettings = useShopSettings(state => state.load);
  const [truck, setTruck] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const found = await findEstimateByToken(cleanToken);
        if (!cancelled) {
          setTruck(found.truck);
          setEstimate(clean(found.estimate));
          if (!found.estimate) setError('Estimate link not found.');
        }
      } catch (err) {
        if (!cancelled) setError('We could not load this estimate. Please call the shop.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [cleanToken]);

  async function saveEstimate(nextEstimate, activity) {
    setSaving(true);
    const estimates = clean(truck.estimates || []).map(item =>
      item.id === nextEstimate.id ? nextEstimate : item
    );
    const activities = activity
      ? [activity, ...clean(truck.activities || [])]
      : clean(truck.activities || []);
    await client.graphql({
      query: mutations.updateTruck,
      variables: { input: { id: truck.id, estimates, activities, lastNote: nowSeconds() } },
    });
    setTruck(current => ({ ...current, estimates, activities, lastNote: nowSeconds() }));
    setEstimate(nextEstimate);
    setSaving(false);
  }

  async function approve() {
    if (!estimate || saving) return;
    const ts = nowSeconds();
    const next = clean({
      ...estimate,
      status: 'approved',
      approvedAt: ts,
      approvalMethod: 'email_link',
      approvedBy: 'Customer (online)',
      updatedAt: ts,
    });
    await saveEstimate(next, {
      id: nowId('act'),
      type: 'authorization',
      text: `Estimate approved by customer online - ${money(next.total)}`,
      author: 'Customer (online)',
      timestamp: ts,
      customerFacing: true,
      stage: truck.status || null,
    });
    setDone('approved');
  }

  async function decline() {
    if (!estimate || saving) return;
    const ts = nowSeconds();
    const next = clean({
      ...estimate,
      status: 'declined',
      declinedAt: ts,
      declineReason,
      updatedAt: ts,
    });
    await saveEstimate(next, {
      id: nowId('act'),
      type: 'note',
      text: `Estimate declined by customer online${declineReason ? ` - ${declineReason}` : ''}`,
      author: 'Customer (online)',
      timestamp: ts,
      customerFacing: true,
      stage: truck.status || null,
    });
    setDone('declined');
  }

  const shopName = settings?.business?.shopName || settings?.business?.companyName || 'REKON';

  if (loading) return <main className="eap-page"><div className="eap-card">Loading estimate...</div></main>;
  if (error || !truck || !estimate) return <main className="eap-page"><div className="eap-card eap-error">{error || 'Estimate not found.'}</div></main>;
  if (done === 'approved') {
    return <main className="eap-page"><div className="eap-card eap-confirm"><h1>Thank you.</h1><p>We'll begin work shortly. {shopName} will contact you with updates.</p></div></main>;
  }
  if (done === 'declined') {
    return <main className="eap-page"><div className="eap-card eap-confirm"><h1>Understood.</h1><p>We'll reach out to discuss your options.</p></div></main>;
  }

  return (
    <main className="eap-page">
      <EstimateDocument truck={truck} estimate={estimate} settings={settings} />
      <section className="eap-actions">
        {!declining ? (
          <>
            <button className="eap-approve" onClick={approve} disabled={saving || estimate.status === 'approved'}>
              Approve This Estimate
            </button>
            <button className="eap-decline" onClick={() => setDeclining(true)} disabled={saving || estimate.status === 'declined'}>
              Decline
            </button>
          </>
        ) : (
          <div className="eap-decline-box">
            <label>
              Let us know why you're declining (optional)
              <textarea value={declineReason} onChange={event => setDeclineReason(event.target.value)} rows={4} />
            </label>
            <div>
              <button className="eap-decline" onClick={decline} disabled={saving}>Confirm Decline</button>
              <button className="eap-cancel" onClick={() => setDeclining(false)} disabled={saving}>Cancel</button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
